import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/_components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/_components/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { JsonViewerDialog } from "@/components/json-viewer-dialog";
import { API_CONFIG } from "@/constants/api";
import { customInstance } from "@/lib/axios-instance";
import { UI_MESSAGES } from "@/constants/ui-constants";
import { RefreshCw } from "lucide-react";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";

interface SQSMessage {
  messageBody: string;
  sentTimestamp: string;
  messageId: string;
  correlationId: string;
}

const QUEUE_OPTIONS = [
  { value: "candidate-outreach-outcome", label: "Recruitment Outcome" },
  { value: "publish-im", label: "Publish IM" },
  { value: "serviceo-app-response", label: "Serviceo App Response" },
  { value: "tpmo-outcome", label: "TPMO Outcome" },
];

export default function ViewSQSPage() {
  return (
    <PageAccessGuard authority="ADMIN_TESTING_VIEW">
      <ViewSQSPageContent />
    </PageAccessGuard>
  );
}

function ViewSQSPageContent() {
  const [selectedQueue, setSelectedQueue] = useState<string>("");
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery<SQSMessage[]>({
    queryKey: ["sqs-data", selectedQueue],
    queryFn: async () => {
      if (!selectedQueue) return [];
      const response = await customInstance<SQSMessage[]>({
        url: `${API_CONFIG.ENDPOINTS.TEMP_TPMO_VIEW_SQS}?queueName=${selectedQueue}`,
        method: "GET",
      });
      return response.data;
    },
    enabled: !!selectedQueue,
  });

  const handleQueueChange = (value: string) => {
    setSelectedQueue(value);
  };

  const handleMessageBodyClick = (messageBody: string) => {
    try {
      const parsedBody = JSON.parse(messageBody);
      setSelectedMessage(parsedBody);
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Failed to parse message body:", error);
      setSelectedMessage({ raw: messageBody });
      setIsDialogOpen(true);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return timestamp;
    }
  };

  if (isLoading) {
    return <div className="p-6">{UI_MESSAGES.LOADING}</div>;
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">View SQS Data</h1>
            <p className="text-gray-600 mt-2">View and analyze SQS queue messages</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Select Queue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="flex-1 max-w-md">
                <Select value={selectedQueue} onValueChange={handleQueueChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a queue" />
                  </SelectTrigger>
                  <SelectContent>
                    {QUEUE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedQueue && (
                <Button
                  onClick={() => refetch()}
                  variant="outline"
                  className="h-10"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {error && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8 text-red-600">
                {error instanceof Error ? error.message : "Failed to load SQS data"}
              </div>
            </CardContent>
          </Card>
        )}

        {!error && selectedQueue && data && data.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Messages ({data.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 hover:bg-gray-50">
                    <TableHead className="font-semibold text-gray-900">Message ID</TableHead>
                    <TableHead className="font-semibold text-gray-900">Correlation ID</TableHead>
                    <TableHead className="font-semibold text-gray-900">Sent Timestamp</TableHead>
                    <TableHead className="font-semibold text-gray-900">Message Body</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((message) => (
                    <TableRow key={message.messageId}>
                      <TableCell className="font-mono text-xs">
                        {message.messageId}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {message.correlationId || "N/A"}
                      </TableCell>
                      <TableCell>{formatTimestamp(message.sentTimestamp)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 h-8 px-2"
                          onClick={() => handleMessageBodyClick(message.messageBody)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {!error && selectedQueue && data && data.length === 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8 text-gray-500">
                No messages found in the selected queue
              </div>
            </CardContent>
          </Card>
        )}

        {!selectedQueue && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8 text-gray-500">
                Please select a queue to view messages
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <JsonViewerDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        title="Message Body"
        data={selectedMessage}
      />
    </div>
  );
}
